import { API, graphqlOperation, Auth } from "aws-amplify";

import pako from "pako";

export default class ApiClient {
  constructor({ onError = () => {} }) {
    this.onError = onError;
  }

  async request({ query, payload = {} }) {
    try {
      const res = await API.graphql(graphqlOperation(query, { ...payload }));

      return res;
    } catch (error) {
      this.onError(error);

      throw error;
    }
  }

  async postStreamAndDecompress(url, data, options = {}) {
    const { idleTimeoutMs = 30 * 1000, signal } = options;

    const controller = new AbortController();
    const abortStream = () => controller.abort();

    let idleTimeoutId;
    let reader;

    const resetIdleTimeout = () => {
      clearTimeout(idleTimeoutId);
      idleTimeoutId = setTimeout(() => controller.abort(), idleTimeoutMs);
    };

    try {
      if (signal?.aborted) {
        controller.abort();
      } else {
        signal?.addEventListener("abort", abortStream);
      }

      resetIdleTimeout();

      const session = await Auth.currentSession();

      if (controller.signal.aborted) {
        return;
      }

      const cognitoAccessToken = session.getAccessToken().getJwtToken();

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: cognitoAccessToken,
        },
        body: JSON.stringify({
          ...data,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      reader = response.body.getReader();
      const chunks = [];
      let totalLength = 0;

      while (true) {
        resetIdleTimeout();
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        totalLength += value.length;
      }

      clearTimeout(idleTimeoutId);

      const compressed = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        compressed.set(chunk, offset);
        offset += chunk.length;
      }

      // use pako to decompress the zlib data
      const decompressed = pako.inflate(compressed);

      const text = new TextDecoder("utf-8").decode(decompressed);

      return JSON.parse(text);
    } catch (err) {
      clearTimeout(idleTimeoutId);

      if (!controller.signal.aborted) {
        this.onError(err);
      }

      throw err;
    } finally {
      clearTimeout(idleTimeoutId);
      signal?.removeEventListener("abort", abortStream);

      if (controller.signal.aborted && reader) {
        await reader.cancel().catch(() => {});
      }
    }
  }
}
